import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Activity, 
  CheckCircle2, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  FileWarning,
  MessageSquare,
  Users,
  Code
} from "lucide-react";

export interface BehaviorDiagnostics {
  totalEvents?: number;
  uniqueAnimals?: number;
  parseWarnings?: string[];
}

interface BehaviorDiagnosticsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diagnostics: BehaviorDiagnostics | null | undefined;
  location: string;
  reportDate: string;
  scoredCount?: number;
  unscoredCount?: number;
  scoreCounts?: [string, number][];
}

export function BehaviorDiagnosticsDrawer({
  open,
  onOpenChange,
  diagnostics,
  location,
  reportDate,
  scoredCount = 0,
  unscoredCount = 0,
  scoreCounts = [],
}: BehaviorDiagnosticsDrawerProps) {
  const [showRawDiagnostics, setShowRawDiagnostics] = useState(false);

  const totalEvents = diagnostics?.totalEvents ?? 0;
  const uniqueAnimals = diagnostics?.uniqueAnimals ?? 0;
  const parseWarnings = diagnostics?.parseWarnings ?? [];
  
  const hasWarnings = parseWarnings.length > 0;
  const isHealthy = !hasWarnings;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="behavior-diagnostics-drawer">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Interaction Report Diagnostics
          </SheetTitle>
          <SheetDescription>
            {location} — {reportDate}
          </SheetDescription>
        </SheetHeader>

        {!diagnostics ? (
          <div className="py-8 text-center" data-testid="diagnostics-unavailable">
            <FileWarning className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Diagnostics were not recorded for this report.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <Card data-testid="health-status-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Report Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isHealthy ? (
                  <Badge variant="default" className="gap-1" data-testid="badge-health-ok">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    OK
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 border-amber-500 text-amber-600 dark:text-amber-400" data-testid="badge-health-warnings">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {parseWarnings.length} Warning{parseWarnings.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </CardContent>
            </Card>

            <Card data-testid="counts-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Parsing Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Total interactions
                  </span>
                  <span className="font-medium text-right" data-testid="text-total-events">
                    {totalEvents}
                  </span>

                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" />
                    Unique animals
                  </span>
                  <span className="font-medium text-right" data-testid="text-unique-animals">
                    {uniqueAnimals}
                  </span>
                </div>
              </CardContent>
            </Card>

            {(scoredCount > 0 || unscoredCount > 0) && (
              <Card data-testid="scoring-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Behavior Scoring
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Scored interactions</span>
                    <span className="font-medium text-right" data-testid="text-scored-count">
                      {scoredCount}
                    </span>

                    <span className="text-muted-foreground">Unscored interactions</span>
                    <span className="font-medium text-right" data-testid="text-unscored-count">
                      {unscoredCount}
                    </span>
                  </div>

                  {scoreCounts.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Score breakdown</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {scoreCounts.map(([scoreCode, count]) => (
                          <Badge key={scoreCode} variant="outline" className="gap-1">
                            {count}x Score {scoreCode}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {parseWarnings.length > 0 && (
              <Card data-testid="warnings-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Parse Warnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {parseWarnings.map((warning, index) => (
                      <li 
                        key={index} 
                        className="text-sm text-amber-600 dark:text-amber-400 p-2 bg-amber-50 dark:bg-amber-950/30 rounded"
                        data-testid={`warning-${index}`}
                      >
                        {warning}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between text-muted-foreground"
                onClick={() => setShowRawDiagnostics(!showRawDiagnostics)}
                data-testid="button-toggle-raw"
              >
                <span className="flex items-center gap-1.5">
                  <Code className="h-3.5 w-3.5" />
                  Technical details (DEV)
                </span>
                {showRawDiagnostics ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>

              {showRawDiagnostics && (
                <pre
                  className="mt-3 p-3 text-xs bg-muted rounded overflow-auto max-h-64"
                  data-testid="raw-diagnostics-json"
                >
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
